import React, { useState, useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_CONFIG, add404ErrorInterceptor } from '../../../../utils/apiConfig';
import ProductSelector from '../../../Product/ProductSelector.js';
import cancelledWatermark from '../../../../assets/images/cancelled.png';
import defaultLogo from '../../../../assets/images/Wyenfos_bills_logo.png';
import wyenfosInfotechLogo from '../../../../assets/company-logos/wyenfos_infotech.png';
import wyenfosLogo from '../../../../assets/company-logos/wyenfos.png';
import wyenfosAdsLogo from '../../../../assets/company-logos/wyenfos_ads.png';
import wyenfosCashLogo from '../../../../assets/company-logos/wyenfos_cash.png';
import wyenfosGoldLogo from '../../../../assets/company-logos/wyenfos_gold.png';
import ayurLifeLogo from '../../../../assets/company-logos/Ayur4life_logo.png';
import wyenfosPureDropsLogo from '../../../../assets/company-logos/wyenfos pure drops.png';
import './CreditBill.css';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';
import debounce from 'lodash.debounce';
import { getCompanyDetails } from '../utils/companyHelpers.js';
import { QRCodeCanvas } from 'qrcode.react';
import FirebaseService from '../../../../services/firebaseService.js';
import { useAuth } from '../../../../contexts/AuthContext';

// Get user info from localStorage as fallback (moved inside component)

const companyPrefixes = {
  'WYENFOS INFOTECH': 'WIT',
  'WYENFOS GOLD AND DIAMONDS': 'WGD',
  'WYENFOS ADS': 'WAD',
  'WYENFOS CASH VAPASE': 'WCV',
  'AYUR4LIFE HERBALS INDIA': 'ALH',
  'WYENFOS': 'WNF',
  'WYENFOS PURE DROPS': 'WPD',
};

const validCompanies = [
  'WYENFOS INFOTECH',
  'WYENFOS GOLD AND DIAMONDS',
  'WYENFOS ADS',
  'WYENFOS CASH VAPASE',
  'AYUR4LIFE HERBALS INDIA',
  'WYENFOS',
  'WYENFOS PURE DROPS',
  'WYENFOS CHARITABLE TRUST',
];

const api = axios.create({
  baseURL: `${API_CONFIG.BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add 404 error handling interceptor
add404ErrorInterceptor(api);

const ErrorBoundary = ({ children }) => {
  const [error, setError] = useState(null);

  const handleReset = () => setError(null);

  if (error) {
    return (
      <div className="error-boundary">
        <h2>Something went wrong</h2>
        <p>{error.message}</p>
        <button onClick={handleReset}>Try again</button>
      </div>
    );
  }

  return children;
};

const formReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map((item, i) =>
          i === action.index ? { ...item, [action.field]: action.value } : item
        ),
      };
    case 'UPDATE_DUE_DATE':
      return {
        ...state,
        dueDates: state.dueDates.map((due, i) =>
          i === action.index ? { ...due, [action.field]: action.value } : due
        ),
      };
    case 'ADD_ITEM':
      return { ...state, items: [...state.items, action.newItem] };
    case 'ADD_DUE_DATE':
      return { ...state, dueDates: [...state.dueDates, action.newDueDate] };
    case 'SET_FORM':
      return { ...action.form };
    default:
      return state;
  }
};

const CreditBill = () => {
  const { currentUser, userProfile } = useAuth();
  const { state: locationState } = useLocation();
  const [searchParams] = useSearchParams();
  
  // Use Firebase auth user info if available, fallback to localStorage
  const currentUserEmail = currentUser?.email || localStorage.getItem('userEmail') || 'unknown@example.com';
  const currentUserRole = userProfile?.role || localStorage.getItem('userRole') || 'staff';
  
  // Handle company selection from URL parameters or location state
  let selectedCompanyFromState = 'WYENFOS';
  
  // First try to get company from URL parameters
  const companyFromUrl = searchParams.get('company');
  if (companyFromUrl) {
    selectedCompanyFromState = decodeURIComponent(companyFromUrl);
  } else if (locationState?.selectedCompany) {
    // Fallback to location state for backward compatibility
    // If selectedCompany is an object, extract the name
    if (typeof locationState.selectedCompany === 'object' && locationState.selectedCompany.name) {
      selectedCompanyFromState = locationState.selectedCompany.name;
    } else if (typeof locationState.selectedCompany === 'string') {
      selectedCompanyFromState = locationState.selectedCompany;
    }
  }
  
  // Handle company name mapping for backward compatibility
  if (selectedCompanyFromState === 'AYUR 4 LIFE HERBALS India') {
    selectedCompanyFromState = 'AYUR4LIFE HERBALS INDIA';
  }
  
  // Handle company name mapping for WYENFOS GOLD
  if (selectedCompanyFromState === 'WYENFOS GOLD & DIAMONDS') {
    selectedCompanyFromState = 'WYENFOS GOLD AND DIAMONDS';
  }
  
  // Ensure selectedCompanyFromState is always a string
  if (typeof selectedCompanyFromState !== 'string') {
    console.warn('selectedCompanyFromState is not a string:', selectedCompanyFromState);
    selectedCompanyFromState = 'WYENFOS';
  }
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedBills, setSavedBills] = useState([]);
  const [editPermissions, setEditPermissions] = useState({});
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingDueDateIndex, setEditingDueDateIndex] = useState(null);
  const [currentBillId, setCurrentBillId] = useState(null);
  const [pendingEditBill, setPendingEditBill] = useState(null);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualEntryIndex, setManualEntryIndex] = useState(null);
  const [manualEntryData, setManualEntryData] = useState({
    description: '',
    code: '',
    hsnSac: '',
    quantity: 1,
    rate: '',
    taxRate: 18
  });
  const [manualEditMode, setManualEditMode] = useState(false);
  const [editModeToggle, setEditModeToggle] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Bank details and QR code state
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountNumber: '',
    ifsc: '',
    swiftCode: '',
    branch: '',
    upiId: '',
    qrCodeUrl: ''
  });
  const [qrCodeData, setQrCodeData] = useState('');

  const sanitizeInput = useCallback((input) => DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }), []);

  // Dual-logo companies configuration - memoized to prevent re-creation on every render
  const dualLogoCompanies = useMemo(() => ({
    'WYENFOS ADS': {
      mainLogo: wyenfosLogo,        // WYENFOS main logo (primary)
      centerLogo: wyenfosAdsLogo,   // WYENFOS ADS specific logo (secondary)
      centerLogoAlt: 'WYENFOS ADS Logo'
    },
    'WYENFOS CASH VAPASE': {
      mainLogo: wyenfosLogo,        // WYENFOS main logo (primary)
      centerLogo: wyenfosCashLogo,  // CASH VAPASE specific logo (secondary)
      centerLogoAlt: 'WYENFOS CASH VAPASE Logo'
    }
  }), []);

  // Get reliable logo for current company
  const getCompanyLogo = useCallback((companyName = selectedCompanyFromState) => {
    console.log('🔍 Debug - selectedCompanyFromState:', companyName);
    
    const logoMap = {
      'WYENFOS INFOTECH': wyenfosInfotechLogo, // WYENFOS INFOTECH specific logo
      'WYENFOS GOLD AND DIAMONDS': wyenfosGoldLogo, // WYENFOS GOLD specific logo
      'WYENFOS ADS': wyenfosLogo, // Primary logo for dual-logo companies
      'WYENFOS CASH VAPASE': wyenfosLogo, // Primary logo for dual-logo companies
      'AYUR4LIFE HERBALS INDIA': ayurLifeLogo, // AYUR specific logo
      'WYENFOS': wyenfosLogo, // Main WYENFOS logo
      'WYENFOS PURE DROPS': wyenfosPureDropsLogo, // PURE DROPS specific logo
    };
    
    const logo = logoMap[companyName] || defaultLogo;
    console.log('🖼️ Final selected logo for', companyName, ':', logo);
    return logo;
  }, [selectedCompanyFromState]);

  // Function to get logo configuration for dual-logo companies
  const getLogoConfiguration = useCallback((companyName) => {
    if (dualLogoCompanies[companyName]) {
      return {
        isDualLogo: true,
        mainLogo: dualLogoCompanies[companyName].mainLogo,
        centerLogo: dualLogoCompanies[companyName].centerLogo,
        centerLogoAlt: dualLogoCompanies[companyName].centerLogoAlt
      };
    }
    
    return {
      isDualLogo: false,
      mainLogo: getCompanyLogo(companyName),
      centerLogo: null,
      centerLogoAlt: null
    };
  }, [dualLogoCompanies, getCompanyLogo]);

  // Fetch bank details for QR code generation from Firebase
  const fetchBankDetails = useCallback(async () => {
    const companyName = selectedCompanyFromState;
    if (!companyName) return;
    
    console.log('CreditBill: Fetching bank details from Firebase for company:', companyName);
    
    try {
      // First try to fetch from Firebase bank details collection
      const firebaseBankDetails = await FirebaseService.getBankDetails(companyName);
      
      if (firebaseBankDetails) {
        console.log('✅ CreditBill: Found bank details in Firebase:', firebaseBankDetails);
        
        const bankData = {
          bankName: firebaseBankDetails.bankName || '',
          accountNumber: firebaseBankDetails.accountNumber || '',
          ifsc: firebaseBankDetails.ifscCode || firebaseBankDetails.ifsc || '',
          swiftCode: firebaseBankDetails.swiftCode || '', // Only use if available in Firebase
          branch: firebaseBankDetails.branch || '',
          upiId: firebaseBankDetails.upiId || '',
          qrCodeUrl: firebaseBankDetails.qrCodeUrl || ''
        };
        
        console.log('🔄 CreditBill: Mapped bank data:', bankData);
        console.log('🔍 CreditBill: IFSC mapping - ifscCode:', firebaseBankDetails.ifscCode, 'ifsc:', firebaseBankDetails.ifsc, 'final:', bankData.ifsc);
        
        setBankDetails(bankData);
        console.log('🏦 CreditBill: Bank details set in state:', bankData);
        
        // Generate QR code data for UPI payment
        if (bankData.upiId) {
          const qrData = `upi://pay?pa=${bankData.upiId}&pn=${companyName}&cu=INR`;
          setQrCodeData(qrData);
          console.log('✅ CreditBill: QR code generated from UPI ID:', qrData);
        } else {
          console.log('⚠️ CreditBill: No UPI ID found, no QR code generated');
          setQrCodeData('');
        }
        
        return;
      }
      
      // No bank details found in Firebase - this is critical for transactions!
      console.error('❌ CreditBill: CRITICAL - No bank details found in Firebase for company:', companyName);
      console.error('❌ CreditBill: This company MUST have bank details in Firebase for transactions!');
      
      // Set empty bank details to avoid showing wrong information
      const emptyBankData = {
        bankName: '',
        accountNumber: '',
        ifsc: '',
        swiftCode: '',
        branch: '',
        upiId: '',
        qrCodeUrl: ''
      };
      
      setBankDetails(emptyBankData);
      setQrCodeData('');
      
      // Show error message to user
      setMessage({ 
        type: 'error', 
        text: `❌ CRITICAL: No bank details found for ${companyName}. Please add bank details to Firebase before processing transactions!` 
      });
      setTimeout(() => setMessage(null), 10000); // Show for 10 seconds
      
    } catch (error) {
      console.error('❌ CreditBill: CRITICAL ERROR fetching bank details:', error);
      console.error('❌ CreditBill: Cannot process transactions without proper bank details!');
      
      // Set empty bank details to avoid showing wrong information
      const emptyBankData = {
        bankName: '',
        accountNumber: '',
        ifsc: '',
        swiftCode: '',
        branch: '',
        upiId: '',
        qrCodeUrl: ''
      };
      
      setBankDetails(emptyBankData);
      setQrCodeData('');
      
      // Show critical error message to user
      setMessage({ 
        type: 'error', 
        text: `❌ CRITICAL ERROR: Cannot fetch bank details for ${companyName}. Check Firebase connection and ensure bank details exist!` 
      });
      setTimeout(() => setMessage(null), 10000); // Show for 10 seconds
    }
  }, [selectedCompanyFromState]);

  // Fetch bank details when component mounts or company changes
  useEffect(() => {
    fetchBankDetails();
  }, [fetchBankDetails]);

  const selectedCompanyDetails = useMemo(() => {
    return getCompanyDetails(selectedCompanyFromState);
  }, [selectedCompanyFromState]);

  const initialFormState = useMemo(
    () => ({
      customerId: '',
      customerName: '',
      customerAddress: 'N/A',
      customerPhone: '',
      customerEmail: '',
      customerGSTIN: '',
      customerContact: {},
      invoiceNo: '',
      date: new Date().toISOString().slice(0, 10),
      dueDates: [
        { id: uuidv4(), date: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10), reminder: false },
      ],
      items: [
        {
          id: uuidv4(),
          code: '',
          description: '',
          hsnSac: '',
          quantity: '1',
          unit: '',
          rate: '',
          gstRate: '18',
          cgstRate: '0',
          cgstAmount: '0',
          sgstRate: '0',
          sgstAmount: '0',
          igstRate: '0',
          igstAmount: '0',
          taxableValue: '0',
          total: '0',
          amountToPay: '0',
          balanceAmount: '0',
        },
      ],
      paymentTerms: 'Cash',
      isOtherState: false,
      applyRoundOff: false,
      cancelled: false,
      remarks: '',
      company: {
        name: selectedCompanyDetails.name || selectedCompanyFromState,
        address: selectedCompanyDetails.address || 'Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001',
        GSTIN: selectedCompanyDetails.GSTIN || '32AAECW1234B1Z0',
        state: selectedCompanyDetails.state || 'Kerala',
        stateCode: selectedCompanyDetails.stateCode || '32',
        mobile: selectedCompanyDetails.mobile || '',
        email: selectedCompanyDetails.email || '',
        website: selectedCompanyDetails.website || '',
        logo: selectedCompanyDetails.logo || '/uploads/wyenfos.png',
      },
      createdBy: currentUserEmail,
      lastUpdatedBy: currentUserEmail,
      paymentDetails: {
        mode: 'Cash',
        status: 'Paid',
        dueDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10),
      },
      overallAmountToPay: '0',
      overallRemainingBalance: '0',
    }),
    [selectedCompanyFromState, selectedCompanyDetails, currentUserEmail]
  );

  const [form, dispatch] = useReducer(formReducer, initialFormState);
  const [errors, setErrors] = useState({
    customerId: '',
    customerName: '',
    customerAddress: '',
    customerPhone: '',
    customerEmail: '',
    customerGSTIN: '',
    invoiceNo: '',
    date: '',
    paymentTerms: '',
    dueDates: [],
    items: [],
  });

  const createNewCustomer = useCallback(async (name, additionalDetails = {}) => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Customer name is required to create a new customer.' });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }
    
    // Check if customer details are provided
    const hasDetails = additionalDetails.address || additionalDetails.phone || additionalDetails.email || additionalDetails.gstin;
    if (!hasDetails) {
      setMessage({ type: 'warning', text: 'Creating customer with name only. Please fill in address, phone, email, or GSTIN for complete customer information.' });
      setTimeout(() => setMessage(null), 5000);
    }
    setLoading(true);
    try {
      const payload = {
        customerName: sanitizeInput(name),
        customerContact: {
          address: additionalDetails.address || form.customerAddress || '',
          phone: additionalDetails.phone || form.customerPhone || '',
          email: additionalDetails.email || form.customerEmail || '',
          gstin: additionalDetails.gstin || form.customerGSTIN || '',
        },
        company: selectedCompanyFromState,
        createdBy: currentUserEmail,
        lastUpdatedBy: currentUserEmail,
      };
      
      let newCustomer;
      
      // Use Firebase when API is not available (production mode)
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        console.log('🔥 Creating customer via Firebase...');
        newCustomer = await FirebaseService.addCustomer(payload);
      } else {
        console.log('🌐 Creating customer via API...');
        const response = await api.post('/customers', payload);
        newCustomer = response.data;
      }
      dispatch({
        type: 'SET_FORM',
        form: {
          ...form,
          customerId: newCustomer.customerId || '',
          customerName: newCustomer.customerName || '',
          customerAddress: newCustomer.customerContact?.address || form.customerAddress || '',
          customerGSTIN: newCustomer.customerContact?.gstin || form.customerGSTIN || '',
          customerEmail: newCustomer.customerContact?.email || form.customerEmail || '',
          customerPhone: newCustomer.customerContact?.phone || form.customerPhone || '',
        }
      });
      setMessage({ type: 'success', text: `Customer created: ${newCustomer.customerName} (${newCustomer.customerId})` });
      setTimeout(() => setMessage(null), 3000);
      
      // If customer was created with minimal details, suggest updating
      if (!hasDetails) {
        setTimeout(() => {
          setMessage({ type: 'info', text: 'Tip: You can update customer details later by editing the customer information.' });
          setTimeout(() => setMessage(null), 4000);
        }, 3000);
      }
      
      return newCustomer.customerId || '';
    } catch (err) {
      console.error('❌ Customer creation failed:', err);
      
      let errorMessage = 'Failed to create customer';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setMessage({ type: 'error', text: `Failed to create customer: ${errorMessage}` });
      setTimeout(() => setMessage(null), 3000);
      return '';
    } finally {
      setLoading(false);
    }
  }, [sanitizeInput, selectedCompanyFromState, form, currentUserEmail]);

  const saveCustomerDetails = useCallback(async () => {
    // Validate required customer information
    if (!form.customerName.trim()) {
      setMessage({ type: 'error', text: 'Customer name is required to save customer details.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // Check if customer has meaningful details to save
    const hasDetails = form.customerAddress.trim() || form.customerPhone.trim() || 
                      form.customerEmail.trim() || form.customerGSTIN.trim();
    
    if (!hasDetails) {
      setMessage({ type: 'warning', text: 'Please fill in at least one detail (address, phone, email, or GSTIN) to save customer.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // Show confirmation dialog
    const shouldSave = window.confirm(
      `Do you want to save customer "${form.customerName}" with the current details?\n\n` +
      `Address: ${form.customerAddress || 'Not provided'}\n` +
      `Phone: ${form.customerPhone || 'Not provided'}\n` +
      `Email: ${form.customerEmail || 'Not provided'}\n` +
      `GSTIN: ${form.customerGSTIN || 'Not provided'}`
    );

    if (!shouldSave) return;

    setLoading(true);
    try {
      // Debug: Log the current form state
      console.log('📋 Current form state before saving:', {
        customerName: form.customerName,
        customerAddress: form.customerAddress,
        customerPhone: form.customerPhone,
        customerEmail: form.customerEmail,
        customerGSTIN: form.customerGSTIN,
      });

      const customerData = {
        customerName: sanitizeInput(form.customerName),
        customerContact: {
          address: form.customerAddress || '',
          phone: form.customerPhone || '',
          email: form.customerEmail || '',
          gstin: form.customerGSTIN || '',
        },
        company: selectedCompanyFromState,
        createdBy: currentUserEmail,
        lastUpdatedBy: currentUserEmail,
      };

      console.log('💾 Saving customer details:', customerData);
      console.log('🔍 Customer contact details:', customerData.customerContact);

      let savedCustomer;
      
      // Use Firebase when API is not available (production mode)
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        console.log('🔥 Saving customer via Firebase...');
        savedCustomer = await FirebaseService.addCustomer(customerData);
      } else {
        console.log('🌐 Saving customer via API...');
        const response = await api.post('/customers', customerData);
        savedCustomer = response.data;
      }

      // Update form with the saved customer ID if it was generated
      if (savedCustomer.customerId && !form.customerId) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'customerId',
          value: savedCustomer.customerId
        });
      }

      setMessage({ 
        type: 'success', 
        text: `Customer "${savedCustomer.customerName}" saved successfully! ${savedCustomer.customerId ? `(ID: ${savedCustomer.customerId})` : ''}` 
      });
      setTimeout(() => setMessage(null), 4000);

    } catch (err) {
      console.error('❌ Failed to save customer:', err);
      
      let errorMessage = 'Failed to save customer';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setMessage({ type: 'error', text: `Failed to save customer: ${errorMessage}` });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  }, [form, sanitizeInput, selectedCompanyFromState, currentUserEmail]);

  const searchCustomers = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      setSearchResults([]);
      setMessage({ type: 'warning', text: 'Please enter a customer name to search' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setIsCustomerLoading(true);
    try {
      console.log('🔍 Searching customers for:', query.trim());
      
      let customers = [];
      
      // Use Firebase when API is not available (production mode)
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        console.log('🔥 Using Firebase for customer search');
        const allCustomers = await FirebaseService.getCustomers();
        const searchTerm = sanitizeInput(query.trim()).toLowerCase();
        customers = allCustomers.filter(customer => 
          customer.customerName?.toLowerCase().includes(searchTerm) ||
          customer.customerId?.toLowerCase().includes(searchTerm) ||
          customer.customerContact?.phone?.includes(searchTerm) ||
          customer.customerContact?.email?.toLowerCase().includes(searchTerm)
        );
        console.log('🔥 Firebase search results:', customers.length);
      } else {
        console.log('🌐 Using API for customer search');
        const response = await api.get('/customers/find', {
          params: { query: sanitizeInput(query.trim()), company: selectedCompanyFromState },
        });
        customers = Array.isArray(response.data) ? response.data : [];
        console.log('🌐 API search results:', customers.length);
      }
      
      setSearchResults(customers);
      
      // If no customers found, show info message
      if (customers.length === 0) {
        setMessage({ type: 'info', text: `No customer found with name "${query}". You can continue by filling customer details manually.` });
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'success', text: `Found ${customers.length} customer(s) matching "${query}"` });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Failed to search customers:', error);
      setSearchResults([]);
      setMessage({ type: 'error', text: 'Failed to search customers. Please try again.' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsCustomerLoading(false);
    }
  }, [selectedCompanyFromState, sanitizeInput]);

  // Manual search only - no debouncing
  const handleManualSearch = () => {
    if (customerSearch.trim()) {
      searchCustomers(customerSearch);
    } else {
      setMessage({ type: 'warning', text: 'Please enter a customer name to search' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const selectCustomer = useCallback((customer) => {
    dispatch({
      type: 'SET_FORM',
      form: {
        ...form,
        customerId: customer.customerId || '',
        customerName: customer.customerName || '',
        customerAddress: customer.customerContact?.address || 'N/A',
        customerPhone: customer.customerContact?.phone || '',
        customerEmail: customer.customerContact?.email || '',
        customerGSTIN: customer.customerContact?.gstin || '',
        customerContact: customer.customerContact || {}
      }
    });
    setCustomerSearch('');
    setSearchResults([]);
  }, [form]);

  const getOrGenerateCustomerId = useCallback(async (name) => {
    if (!name.trim()) {
      setErrors((prev) => ({ ...prev, customerName: 'Customer name is required' }));
      return '';
    }
    const sanitizedName = sanitizeInput(name);
    let customer = null;
    try {
      console.log('🔍 Getting or generating customer ID for:', sanitizedName);
      
      // Use Firebase when API is not available (production mode)
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        console.log('🔥 Using Firebase for customer lookup');
        const allCustomers = await FirebaseService.getCustomers();
        const searchTerm = sanitizedName.toLowerCase();
        const matchingCustomers = allCustomers.filter(c => 
          c.customerName?.toLowerCase().includes(searchTerm)
        );
        customer = matchingCustomers.length > 0 ? matchingCustomers[0] : null;
      } else {
        console.log('🌐 Using API for customer lookup');
        const response = await api.get(`/customers/find`, {
          params: { query: sanitizedName, company: selectedCompanyFromState }
        });
        const customers = response.data;
        customer = customers && customers.length > 0 ? customers[0] : null;
      }
      dispatch({
        type: 'SET_FORM',
        form: {
          ...form,
          customerId: customer.customerId || '',
          customerName: customer.customerName || sanitizedName,
          customerAddress: customer.customerContact?.address || 'N/A',
          customerGSTIN: customer.customerContact?.gstin || '',
          customerEmail: customer.customerContact?.email || '',
          customerContact: customer.customerContact || {}
        }
      });
      return customer ? customer.customerId || '' : '';
    } catch (err) {
      if (err.response?.status === 404 || !customer) {
        // No customer found - automatically create new customer with continuous ID
        setMessage({ type: 'info', text: 'Customer not found. Creating new customer automatically...' });
        setTimeout(() => setMessage(null), 3000);
        
        const newCustomerId = await createNewCustomer(sanitizedName, {
          address: form.customerAddress || '',
          phone: form.customerPhone || '',
          email: form.customerEmail || '',
          gstin: form.customerGSTIN || '',
        });
        if (newCustomerId) {
          setMessage({ type: 'success', text: `New customer created automatically: ${name} (${newCustomerId})` });
          setTimeout(() => setMessage(null), 3000);
        }
        return newCustomerId;
      }
      setMessage({ type: 'error', text: `Error fetching customer: ${err.response?.data?.message || err.message}` });
      setTimeout(() => setMessage(null), 3000);
      return '';
    }
  }, [sanitizeInput, createNewCustomer, selectedCompanyFromState, form]);

  // Debounced customer ID generation to avoid creating customers for each character
  const debouncedCustomerIdGeneration = useRef(debounce(async (customerName) => {
    if (customerName && customerName.trim().length >= 3 && !form.customerId) {
      await getOrGenerateCustomerId(customerName);
    }
  }, 1000)).current;

  useEffect(() => {
    if (form.customerName && form.customerName.trim().length >= 3 && !form.customerId) {
      debouncedCustomerIdGeneration(form.customerName);
    }
    return () => debouncedCustomerIdGeneration.cancel();
  }, [form.customerName, form.customerId, debouncedCustomerIdGeneration]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/products', {
        params: { company: selectedCompanyFromState }
      });
      
      // Ensure response.data is an array before mapping
      const productsData = Array.isArray(response.data) ? response.data : [];
      
      setProducts(
        productsData.map(p => ({
          _id: p._id || p.itemCode,
          itemCode: p.itemCode || '',
          itemName: p.itemName || '',
          hsn: p.hsn || '',
          gst: p.gst || '',
          unitPrice: p.unitPrice || 0,
        }))
      );
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
      setMessage({ type: 'error', text: 'Failed to fetch products' });
      setTimeout(() => setMessage(null), 3000);
    }
  }, [selectedCompanyFromState]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchSavedBills = useCallback(async () => {
    try {
      console.log('📄 Fetching saved credit bills from Firebase for company:', selectedCompanyFromState);
      console.log('📄 Current user authentication:', {
        isAuthenticated: !!currentUser,
        userEmail: currentUserEmail,
        userRole: currentUserRole,
        userUID: currentUser?.uid
      });
      
      // Use Firebase for fetching credit bills
      const firebaseBills = await FirebaseService.getCreditBills(selectedCompanyFromState, 10);
      setSavedBills(firebaseBills);
      
      console.log('✅ Fetched', firebaseBills.length, 'credit bills from Firebase');
    } catch (err) {
      console.error('❌ Error fetching saved bills:', err);
      console.error('❌ Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      setSavedBills([]);
      setMessage({ type: 'error', text: `Failed to fetch saved bills from Firebase: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  }, [selectedCompanyFromState, currentUser, currentUserEmail, currentUserRole]);

  useEffect(() => {
    fetchSavedBills();
  }, [fetchSavedBills, selectedCompanyFromState]);

  const generateInvoiceNumber = useCallback(async () => {
    if (!validCompanies.includes(selectedCompanyFromState)) {
      setMessage({ type: 'error', text: `Invalid company: ${selectedCompanyFromState}. Using fallback invoice number.` });
      setTimeout(() => setMessage(null), 3000);
      return `${companyPrefixes[selectedCompanyFromState] || 'WNF'}-1`;
    }
    try {
      const response = await api.get('/creditbills/latest-invoice', {
        params: { company: selectedCompanyFromState }
      });
      const latestInvoice = response.data?.data?.invoiceNo;
      if (!latestInvoice) {
        return `${companyPrefixes[selectedCompanyFromState]}-1`; // Start with -1 if no bills exist
      }
      
      // The server already returns the correct next invoice number
      // No need to increment it again on the client side
      
      return latestInvoice;
    } catch (err) {
      console.error('Error generating invoice number:', err);
      return `${companyPrefixes[selectedCompanyFromState]}-1`; // Fallback to -1 on error
    }
  }, [selectedCompanyFromState]);

  useEffect(() => {
    const initializeForm = async () => {
      setLoading(true);
      try {
        const invoiceNo = await generateInvoiceNumber();
        dispatch({ type: 'UPDATE_FIELD', field: 'invoiceNo', value: invoiceNo });
      } catch (err) {
        const fallbackInvoiceNo = `${companyPrefixes[selectedCompanyFromState] || 'WNF'}-1`;
        dispatch({ type: 'UPDATE_FIELD', field: 'invoiceNo', value: fallbackInvoiceNo });
        setMessage({ type: 'error', text: `Failed to generate invoice number: ${err.message}` });
        setTimeout(() => setMessage(null), 3000);
      } finally {
        setLoading(false);
      }
    };
    initializeForm();
  }, [generateInvoiceNumber, selectedCompanyFromState]);

  // Ensure invoice number is set when form is reset
  useEffect(() => {
    if (!form.invoiceNo || form.invoiceNo === '') {
      const fallbackInvoiceNo = `${companyPrefixes[selectedCompanyFromState] || 'WNF'}-1`;
      dispatch({ type: 'UPDATE_FIELD', field: 'invoiceNo', value: fallbackInvoiceNo });
    }
  }, [form.invoiceNo, selectedCompanyFromState]);

  const handleProductSelect = useCallback((product, itemIndex) => {
    // Set user interaction flag
    setHasUserInteracted(true);
    
    // Clear validation errors for this item when selecting a product
    setErrors(prev => ({
      ...prev,
      items: prev.items.map((itemErrors, index) => 
        index === itemIndex 
          ? { ...itemErrors, code: '', description: '', hsnSac: '', rate: '', quantity: '', gstRate: '', amountToPay: '' }
          : itemErrors
      )
    }));
    
    // Handle clearing product (when product is null)
    if (!product) {
      dispatch({
        type: 'UPDATE_ITEM',
        index: itemIndex,
        field: 'code',
        value: ''
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: itemIndex,
        field: 'description',
        value: ''
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: itemIndex,
        field: 'hsnSac',
        value: ''
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: itemIndex,
        field: 'gstRate',
        value: '18'
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: itemIndex,
        field: 'rate',
        value: ''
      });
      return;
    }
    
    // Handle selecting a product
    dispatch({
      type: 'UPDATE_ITEM',
      index: itemIndex,
      field: 'code',
      value: product.itemCode || ''
    });
    dispatch({
      type: 'UPDATE_ITEM',
      index: itemIndex,
      field: 'description',
      value: product.itemName || ''
    });
    dispatch({
      type: 'UPDATE_ITEM',
      index: itemIndex,
      field: 'hsnSac',
      value: product.hsn || ''
    });
    dispatch({
      type: 'UPDATE_ITEM',
      index: itemIndex,
      field: 'gstRate',
      value: product.gst ? product.gst.toString() : '18'
    });
    dispatch({
      type: 'UPDATE_ITEM',
      index: itemIndex,
      field: 'rate',
      value: product.unitPrice ? product.unitPrice.toString() : '0'
    });
    // setShowProductDropdown(false);
    // setProductSearchTerm('');
  }, []);

  const openManualEntryModal = useCallback((index) => {
    const currentItem = form.items[index];
    setManualEntryIndex(index);
    setManualEntryData({
      description: currentItem.description || '',
      code: currentItem.code || '',
      hsnSac: currentItem.hsnSac || '',
      quantity: currentItem.quantity || 1,
      rate: currentItem.rate || '',
      taxRate: currentItem.gstRate || 18
    });
    setShowManualEntryModal(true);
  }, [form.items]);

  const closeManualEntryModal = useCallback(() => {
    setShowManualEntryModal(false);
    setManualEntryIndex(null);
    setManualEntryData({
      description: '',
      code: '',
      hsnSac: '',
      quantity: 1,
      rate: '',
      taxRate: 18
    });
  }, []);

  const saveManualEntry = useCallback(() => {
    if (!manualEntryData.description.trim()) {
      setMessage({ type: 'error', text: 'Product description is required.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (manualEntryIndex !== null) {
      // Update all fields using dispatch
      dispatch({
        type: 'UPDATE_ITEM',
        index: manualEntryIndex,
        field: 'description',
        value: manualEntryData.description
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: manualEntryIndex,
        field: 'code',
        value: manualEntryData.code
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: manualEntryIndex,
        field: 'hsnSac',
        value: manualEntryData.hsnSac
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: manualEntryIndex,
        field: 'quantity',
        value: Number(manualEntryData.quantity) || 1
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: manualEntryIndex,
        field: 'rate',
        value: Number(manualEntryData.rate) || 0
      });
      dispatch({
        type: 'UPDATE_ITEM',
        index: manualEntryIndex,
        field: 'gstRate',
        value: Number(manualEntryData.taxRate) || 18
      });
      
      setMessage({ type: 'success', text: 'Product details updated manually.' });
      setTimeout(() => setMessage(null), 3000);
    }
    
    closeManualEntryModal();
  }, [manualEntryData, manualEntryIndex, closeManualEntryModal]);

  const calculateTotals = useCallback(() => {
    const result = {
      totalTaxableValue: 0,
      totalCGST: 0,
      totalSGST: 0,
      totalIGST: 0,
      grandTotal: 0,
      roundOff: 0,
    };

    if (!form.items || !Array.isArray(form.items)) {
      return result;
    }

    form.items.forEach((item) => {
      const quantity = Number(parseFloat(item.quantity)) || 0;
      const rate = Number(parseFloat(item.rate)) || 0;
      const gstRate = Number(parseFloat(item.gstRate)) || 0;

      const taxableValue = quantity * rate;
      const gstAmount = (taxableValue * gstRate) / 100;

      if (form.isOtherState) {
        item.igstRate = gstRate.toString();
        item.igstAmount = gstAmount.toString();
        item.cgstRate = '0';
        item.cgstAmount = '0';
        item.sgstRate = '0';
        item.sgstAmount = '0';
      } else {
        item.cgstRate = (gstRate / 2).toString();
        item.cgstAmount = (gstAmount / 2).toString();
        item.sgstRate = (gstRate / 2).toString();
        item.sgstAmount = (gstAmount / 2).toString();
        item.igstRate = '0';
        item.igstAmount = '0';
      }

      item.taxableValue = taxableValue.toFixed(2);
      item.total = (taxableValue + gstAmount).toFixed(2);

      result.totalTaxableValue += taxableValue;
      result.totalCGST += Number(parseFloat(item.cgstAmount)) || 0;
      result.totalSGST += Number(parseFloat(item.sgstAmount)) || 0;
      result.totalIGST += Number(parseFloat(item.igstAmount)) || 0;
      result.grandTotal += Number(parseFloat(item.total)) || 0;
    });

    result.grandTotal = form.applyRoundOff ? Math.round(result.grandTotal) : result.grandTotal;
    result.roundOff = result.grandTotal - (result.totalTaxableValue + result.totalCGST + result.totalSGST + result.totalIGST);

    Object.keys(result).forEach((key) => {
      result[key] = Number(result[key]);
      if (isNaN(result[key])) result[key] = 0;
    });

    return result;
  }, [form.items, form.isOtherState, form.applyRoundOff]);

  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setMessage({ type: 'error', text: 'Notifications not supported' });
      setTimeout(() => setMessage(null), 3000);
      return false;
    }
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (err) {
      setMessage({ type: 'error', text: 'Notification permission failed' });
      setTimeout(() => setMessage(null), 3000);
      return false;
    }
  }, []);

  const scheduleReminder = useCallback((dueDate, index) => {
    if (!('Notification' in window)) {
      setMessage({ type: 'error', text: 'Notifications not supported by your browser.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    const dueDateTime = new Date(dueDate).getTime();
    const now = new Date().getTime();
    const timeUntilDue = dueDateTime - now;

    if (timeUntilDue <= 0) {
      setMessage({ type: 'error', text: `Due date ${index + 1} is in the past.` });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (Notification.permission === 'granted') {
      setTimeout(() => {
        if (form.dueDates[index]?.reminder) {
          new Notification(`Credit Bill Reminder`, {
            body: `Due date ${index + 1} for bill ${form.invoiceNo} is today!`,
          });
        }
      }, timeUntilDue);
      setMessage({ type: 'success', text: `Reminder set for due date ${index + 1}` });
      setTimeout(() => setMessage(null), 3000);
    } else {
      setMessage({ type: 'error', text: 'Notification permission not granted.' });
      setTimeout(() => setMessage(null), 3000);
    }
  }, [form.dueDates, form.invoiceNo]);

  const toggleReminder = useCallback(async (index, dueDate) => {
    if (!form.dueDates[index].reminder) {
      const granted = await requestNotificationPermission();
      if (!granted) return;
      scheduleReminder(dueDate, index);
    }
    dispatch({
      type: 'UPDATE_DUE_DATE',
      index,
      field: 'reminder',
      value: !form.dueDates[index].reminder
    });
  }, [requestNotificationPermission, scheduleReminder, form]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const sanitizedValue = sanitizeInput(value);

    if (name === 'customerId') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        customerId: sanitizedValue.trim() ? '' : 'Customer ID is required',
      }));
      // Cancel any pending customer ID generation when user manually enters Customer ID
      if (debouncedCustomerIdGeneration.cancel) {
        debouncedCustomerIdGeneration.cancel();
      }
    } else if (name === 'customerName') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        customerName: sanitizedValue.trim() ? '' : 'Customer name is required',
      }));
    } else if (name === 'customerAddress') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        customerAddress: sanitizedValue.trim() ? '' : 'Customer address is required',
      }));
    } else if (name === 'customerPhone') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        customerPhone: sanitizedValue && !/^[0-9]{10}$/.test(sanitizedValue.replace(/\s/g, ''))
          ? 'Invalid phone number format (10 digits required)'
          : '',
      }));
    } else if (name === 'customerEmail') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        customerEmail: sanitizedValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizedValue)
          ? 'Invalid email format'
          : '',
      }));
    } else if (name === 'customerGSTIN') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        customerGSTIN: sanitizedValue && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(sanitizedValue)
          ? 'Invalid GSTIN format'
          : '',
      }));
    } else if (name === 'date') {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: sanitizedValue });
      setErrors((prev) => ({
        ...prev,
        date: sanitizedValue ? '' : 'Date is required',
      }));
    } else if (name === 'paymentTerms') {
      dispatch({
        type: 'UPDATE_FIELD',
        field: name,
        value: sanitizedValue
      });
      dispatch({
        type: 'UPDATE_FIELD',
        field: 'paymentDetails.mode',
        value: sanitizedValue
      });
      setErrors((prev) => ({
        ...prev,
        paymentTerms: sanitizedValue ? '' : 'Payment terms are required',
      }));
    } else if (name === 'overallAmountToPay') {
      const amountToPay = parseFloat(sanitizedValue) || 0;
      const currentTotals = calculateTotals();
      const grandTotal = currentTotals.grandTotal || 0;
      let remainingBalance = Math.max(0, grandTotal - amountToPay);
      
      // Apply round-off to remaining balance if round-off is enabled
      if (form.applyRoundOff) {
        remainingBalance = Math.round(remainingBalance);
      }
      
      // Update the overall amount to pay and remaining balance
      dispatch({ type: 'UPDATE_FIELD', field: 'overallAmountToPay', value: sanitizedValue });
      dispatch({ type: 'UPDATE_FIELD', field: 'overallRemainingBalance', value: remainingBalance.toFixed(2) });
      
      // Update all items to reflect the overall amount distribution
      form.items.forEach((item, index) => {
        const itemTotal = parseFloat(item.total) || 0;
        const itemRatio = grandTotal > 0 ? itemTotal / grandTotal : 0;
        const itemAmountToPay = amountToPay * itemRatio;
        let itemBalance = Math.max(0, itemTotal - itemAmountToPay);
        
        // Apply round-off to item balance if round-off is enabled
        if (form.applyRoundOff) {
          itemBalance = Math.round(itemBalance);
        }
        
        dispatch({ 
          type: 'UPDATE_ITEM', 
          index, 
          field: 'amountToPay', 
          value: itemAmountToPay.toFixed(2) 
        });
        dispatch({ 
          type: 'UPDATE_ITEM', 
          index, 
          field: 'balanceAmount', 
          value: itemBalance.toFixed(2) 
        });
      });
    } else {
      dispatch({ type: 'UPDATE_FIELD', field: name, value: type === 'checkbox' ? checked : sanitizedValue });
    }
  }, [sanitizeInput, debouncedCustomerIdGeneration, calculateTotals, form]);

  const handleItemChange = useCallback((index, e) => {
    const { name, value } = e.target;
    const fieldName = name.split('.')[1];
    const sanitizedValue = sanitizeInput(value);

    // Set user interaction flag when manually editing
    setHasUserInteracted(true);

    dispatch({ type: 'UPDATE_ITEM', index, field: fieldName, value: sanitizedValue });

    const rate = parseFloat(form.items[index].rate) || 0;
    const quantity = parseFloat(form.items[index].quantity) || 0;
    const gstRate = parseFloat(form.items[index].gstRate) || 0;

    const taxableValue = rate * quantity;
    const tax = taxableValue * (gstRate / 100);
    const total = taxableValue + tax;

    if (form.isOtherState) {
      dispatch({ type: 'UPDATE_ITEM', index, field: 'igstRate', value: gstRate.toString() });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'igstAmount', value: tax.toFixed(2) });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'cgstRate', value: '0' });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'cgstAmount', value: '0' });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'sgstRate', value: '0' });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'sgstAmount', value: '0' });
    } else {
      dispatch({ type: 'UPDATE_ITEM', index, field: 'cgstRate', value: (gstRate / 2).toString() });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'cgstAmount', value: (tax / 2).toFixed(2) });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'sgstRate', value: (gstRate / 2).toString() });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'sgstAmount', value: (tax / 2).toFixed(2) });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'igstRate', value: '0' });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'igstAmount', value: '0' });
    }

    dispatch({ type: 'UPDATE_ITEM', index, field: 'taxableValue', value: taxableValue.toFixed(2) });
    dispatch({ type: 'UPDATE_ITEM', index, field: 'total', value: total.toFixed(2) });

    const totalAmount = parseFloat(form.items[index].total || 0);
    if (fieldName === 'amountToPay') {
      const amountToPay = parseFloat(sanitizedValue) || 0;
      dispatch({ type: 'UPDATE_ITEM', index, field: 'amountToPay', value: amountToPay.toFixed(2) });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'balanceAmount', value: (totalAmount - amountToPay).toFixed(2) });
    } else {
      dispatch({ type: 'UPDATE_ITEM', index, field: 'amountToPay', value: totalAmount.toFixed(2) });
      dispatch({ type: 'UPDATE_ITEM', index, field: 'balanceAmount', value: '0.00' });
    }

    setErrors((prev) => {
      const newItemErrors = [...prev.items];
      newItemErrors[index] = {
        id: form.items[index].id,
        code: form.items[index].code.trim() ? '' : 'Item code is required',
        description: form.items[index].description.trim() ? '' : 'Description required',
        hsnSac: form.items[index].hsnSac.trim() ? '' : 'HSN/SAC required',
        rate: isNaN(parseFloat(form.items[index].rate)) || parseFloat(form.items[index].rate) <= 0 ? 'Rate must be > 0' : '',
        quantity: isNaN(parseFloat(form.items[index].quantity)) || parseFloat(form.items[index].quantity) <= 0 ? 'Qty must be > 0' : '',
        gstRate: isNaN(parseFloat(form.items[index].gstRate)) || parseFloat(form.items[index].gstRate) < 0 ? 'GST must be ≥ 0' : '',
        amountToPay:
          isNaN(parseFloat(form.items[index].amountToPay)) || parseFloat(form.items[index].amountToPay) < 0
            ? 'Amt must be ≥ 0'
            : parseFloat(form.items[index].amountToPay) > parseFloat(form.items[index].total)
            ? 'Amt exceeds total'
            : '',
      };
      return { ...prev, items: newItemErrors };
    });
  }, [form.items, form.isOtherState, sanitizeInput]);

  const addItem = useCallback(() => {
    const newItem = {
      id: uuidv4(),
      code: '',
      description: '',
      hsnSac: '',
      quantity: '1',
      unit: 'NOS',
      rate: '',
      gstRate: '',
      cgstRate: '0',
      cgstAmount: '0',
      sgstRate: '0',
      sgstAmount: '0',
      igstRate: '0',
      igstAmount: '0',
      taxableValue: '0.00',
      total: '0.00',
      amountToPay: '0.00',
      balanceAmount: '0.00',
    };
    dispatch({ type: 'ADD_ITEM', newItem });
    setErrors((prev) => ({
      ...prev,
      items: [...prev.items, { id: uuidv4(), code: '', description: '', hsnSac: '', quantity: '', rate: '', gstRate: '', amountToPay: '' }],
    }));
  }, []);

  const deleteItem = useCallback((index) => {
    if (form.items.length === 1) {
      setMessage({ type: 'error', text: 'At least one item is required.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (window.confirm('Delete this item?')) {
      const newItems = form.items.filter((_, i) => i !== index);
      dispatch({ type: 'SET_FORM', form: { ...form, items: newItems } });
      setErrors((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
    }
  }, [form]);

  const handleDueDateChange = useCallback((index, value) => {
    dispatch({ type: 'UPDATE_DUE_DATE', index, field: 'date', value: value });
    const newDueDateErrors = form.dueDates.map((due, i) => ({ id: due.id, date: '' }));
    const today = new Date().toISOString().slice(0, 10);
    form.dueDates.forEach((due, i) => {
      if (!due.date) newDueDateErrors[i].date = `Due date ${i + 1} is required`;
      else if (due.date < today) newDueDateErrors[i].date = `Due date ${i + 1} must be today or later`;
      else if (i > 0 && due.date <= form.dueDates[i - 1].date) newDueDateErrors[i].date = `Due date ${i + 1} must be after due date ${i}`;
    });
    setErrors((prev) => ({ ...prev, dueDates: newDueDateErrors }));
  }, [form.dueDates]);

  const confirmDueDate = useCallback((index) => {
    if (!form.dueDates[index]?.date) {
      setErrors((prev) => {
        const newErrors = [...prev.dueDates];
        newErrors[index] = { ...newErrors[index], date: `Due date ${index + 1} is required` };
        return { ...prev, dueDates: newErrors };
      });
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (form.dueDates[index].date < today) {
      setErrors((prev) => {
        const newErrors = [...prev.dueDates];
        newErrors[index] = { ...newErrors[index], date: `Due date ${index + 1} must be today or later` };
        return { ...prev, dueDates: newErrors };
      });
      return;
    }
    if (index > 0 && form.dueDates[index].date <= form.dueDates[index - 1].date) {
      setErrors((prev) => {
        const newErrors = [...prev.dueDates];
        return { ...prev, dueDates: newErrors };
      });
      return;
    }
    setEditingDueDateIndex(null);
  }, [form.dueDates]);

  const startEditingDueDate = useCallback((index) => setEditingDueDateIndex(index), []);
  const cancelEditingDueDate = useCallback(() => setEditingDueDateIndex(null), []);

  const addDueDate = useCallback(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newDueDate = { id: uuidv4(), date: tomorrow.toISOString().slice(0, 10), reminder: false };
    dispatch({ type: 'ADD_DUE_DATE', newDueDate });
    setErrors((prev) => ({
      ...prev,
      dueDates: [...prev.dueDates, { id: uuidv4(), date: '' }],
    }));
  }, []);

  const validateForm = useCallback((forceValidation = false) => {
    const newErrors = {
      customerId: '',
      customerName: '',
      customerAddress: '',
      customerGSTIN: '',
      invoiceNo: '',
      date: '',
      paymentTerms: '',
      dueDates: form.dueDates.map(() => ({ id: uuidv4(), date: '' })),
      items: form.items.map(() => ({
        id: uuidv4(),
        code: '',
        description: '',
        hsnSac: '',
        rate: '',
        quantity: '',
        gstRate: '',
        amountToPay: '',
      })),
    };

    let hasErrors = false;

    if (!form.customerId) {
      newErrors.customerId = 'Customer ID is required';
      hasErrors = true;
    }
    if (!form.customerName?.trim()) {
      newErrors.customerName = 'Customer name is required';
      hasErrors = true;
    }
    if (!form.customerAddress?.trim()) {
      newErrors.customerAddress = 'Customer address is required';
      hasErrors = true;
    }
    if (
      form.customerGSTIN &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.customerGSTIN)
    ) {
      newErrors.customerGSTIN = 'Invalid GSTIN format';
      hasErrors = true;
    }
    if (!form.invoiceNo?.trim()) {
      newErrors.invoiceNo = 'Invoice number is required';
      hasErrors = true;
    }
    if (!form.date) {
      newErrors.date = 'Date is required';
      hasErrors = true;
    }
    if (!form.paymentTerms) {
      newErrors.paymentTerms = 'Payment terms are required';
      hasErrors = true;
    }

    const today = new Date().toISOString().slice(0, 10);
    form.dueDates.forEach((due, i) => {
      if (!due.date) {
        newErrors.dueDates[i].date = `Due date ${i + 1} is required`;
        hasErrors = true;
      } else if (due.date < today) {
        newErrors.dueDates[i].date = `Due date ${i + 1} must be today or later`;
        hasErrors = true;
      } else if (i > 0 && due.date <= form.dueDates[i - 1].date) {
        newErrors.dueDates[i].date = `Due date ${i + 1} must be after due date ${i}`;
        hasErrors = true;
      }
    });

    form.items.forEach((item, index) => {
      // Only validate if user has interacted or if this is a save operation
      const shouldValidate = hasUserInteracted || forceValidation;
      
      if (shouldValidate) {
        if (!item.code?.trim()) {
          newErrors.items[index].code = 'Item code is required';
          hasErrors = true;
        }
        if (!item.description?.trim()) {
          newErrors.items[index].description = 'Description required';
          hasErrors = true;
        }
        if (!item.hsnSac?.trim()) {
          newErrors.items[index].hsnSac = 'HSN/SAC required';
          hasErrors = true;
        }
        const rate = parseFloat(item.rate);
        if (isNaN(rate) || rate <= 0) {
          newErrors.items[index].rate = 'Rate must be > 0';
          hasErrors = true;
        }
        const qty = parseFloat(item.quantity);
        if (isNaN(qty) || qty <= 0) {
          newErrors.items[index].quantity = 'Qty must be > 0';
          hasErrors = true;
        }
        const gst = parseFloat(item.gstRate);
        if (isNaN(gst) || gst < 0) {
          newErrors.items[index].gstRate = 'GST must be ≥ 0';
          hasErrors = true;
        }
        const amt = parseFloat(item.amountToPay || 0);
        const total = parseFloat(item.total || 0);
        if (isNaN(amt) || amt < 0) {
          newErrors.items[index].amountToPay = 'Amt must be ≥ 0';
          hasErrors = true;
        } else if (amt > total) {
          newErrors.items[index].amountToPay = 'Amt exceeds total';
          hasErrors = true;
        }
      }
    });

    setErrors(newErrors);
    if (hasErrors) {
      setMessage({ type: 'error', text: 'Please correct the highlighted errors' });
      return false;
    }
    setMessage(null);
    return true;
  }, [form]);

  const handleProductInput = (value, index) => {
    // Always update the description field first to allow manual typing
    handleItemChange(index, { target: { name: 'description', value } });
    
    // Then check if it matches a product for auto-fill
    const selectedProduct = products.find(p => p.itemName === value.trim());
    if (selectedProduct) {
      // Only auto-fill other fields if the value exactly matches a product name
      handleProductSelect(selectedProduct, index);
    }
  };

const saveBill = useCallback(async () => {
  if (!validateForm(true)) return; // Force validation on save
  setLoading(true);
  try {

    const totals = calculateTotals();

    // 🔒 Fix for Customer ID validation
    let finalCustomerId = form.customerId;

    // Updated regex to accept CUST-1, CUST-2, etc. format
    if (!/^CUST-\d+$/.test(finalCustomerId)) {
      const newCustomerId = await getOrGenerateCustomerId(form.customerName);
      if (!/^CUST-\d+$/.test(newCustomerId)) {
        setMessage({ type: 'error', text: 'Customer ID creation failed. Cannot save bill.' });
        setLoading(false);
        return;
      }
      finalCustomerId = newCustomerId;
    }
    // Get fresh invoice number
    let freshInvoiceNo;
    
    if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
      console.log('🔥 Using Firebase for credit bill operations...');
      // Use Firebase to get next invoice number
      freshInvoiceNo = await FirebaseService.getLatestCreditBillInvoice(selectedCompanyFromState);
    } else {
      console.log('🌐 Using API for credit bill operations...');
      // Get fresh invoice number from server before saving
      const invoiceResponse = await api.get('/creditbills/latest-invoice', {
        params: { company: selectedCompanyFromState }
      });
      freshInvoiceNo = invoiceResponse.data?.data?.invoiceNo;
    }
    

    const payload = {
      ...form,
      invoiceNo: freshInvoiceNo, // Use fresh invoice number from server
      customerId: finalCustomerId,
      totals: {
        totalTaxableValue: totals.totalTaxableValue,
        totalCGST: totals.totalCGST,
        totalSGST: totals.totalSGST,
        totalIGST: totals.totalIGST,
        grandTotal: totals.grandTotal,
        roundOff: totals.roundOff,
        amountToPay: parseFloat(form.overallAmountToPay) || 0,
        remainingBalance: parseFloat(form.overallRemainingBalance) || 0,
        totalItemAmount: totals.totalTaxableValue,
        subtotal: totals.totalTaxableValue,
      },
      company: {
        name: form.company.name || selectedCompanyFromState,
        address: form.company.address || selectedCompanyDetails.address,
        GSTIN: form.company.GSTIN || selectedCompanyDetails.GSTIN,
        state: form.company.state || selectedCompanyDetails.state,
        stateCode: form.company.stateCode || selectedCompanyDetails.stateCode,
      },
      createdBy: currentUserEmail,
      lastUpdatedBy: currentUserEmail,
    };

    console.log('💾 Saving credit bill with payload:', payload);

    // Save to Firebase or API based on availability
    if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
      console.log('🔥 Saving credit bill to Firebase...');
      await FirebaseService.addCreditBill(payload);
      console.log('✅ Credit bill saved to Firebase successfully');
    } else {
      console.log('🌐 Saving credit bill via API...');
      await api.post('/creditbills', payload);
      console.log('✅ Credit bill saved via API successfully');
    }

    setMessage({ type: 'success', text: 'Credit bill saved successfully to Firebase!' });
    setTimeout(() => setMessage(null), 3000);
    dispatch({ type: 'SET_FORM', form: initialFormState });
    setErrors({
      customerId: '',
      customerName: '',
      customerAddress: '',
      customerPhone: '',
      customerEmail: '',
      customerGSTIN: '',
      invoiceNo: '',
      date: '',
      paymentTerms: '',
      dueDates: [],
      items: [],
      overallAmountToPay: '',
      overallRemainingBalance: '',
    });
    fetchSavedBills();
  } catch (err) {
    console.error("Full error response:", err.response?.data);
    const message = err.response?.data?.message || err.message;
    const validationErrors = err.response?.data?.validationErrors;
    if (validationErrors) {
      console.error("Validation Errors:", validationErrors);
      alert("Validation Errors:\n" + validationErrors.join('\n'));
    }
    setMessage({ type: 'error', text: `Error saving bill: ${message}` });
    setTimeout(() => setMessage(null), 3000);
  } finally {
    setLoading(false);
  }
}, [form, selectedCompanyFromState, selectedCompanyDetails, validateForm, initialFormState, fetchSavedBills, calculateTotals, getOrGenerateCustomerId, currentUserEmail]);

  const sendEmail = useCallback(async () => {

    
    if (!form.customerEmail) {
      setMessage({ type: 'error', text: 'Customer email required' });
      return;
    }
    if (!form.items.length) {
      setMessage({ type: 'error', text: 'Add at least one item' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (!validateForm()) return;

    
    setLoading(true);
    
    // Add timeout for the entire email process
    const emailTimeout = setTimeout(() => {
      setLoading(false);
      setMessage({
        type: 'error',
        text: 'Email request timed out. Please try again.',
      });
      setTimeout(() => setMessage(null), 5000);
    }, 30000); // 30 second timeout
    
    try {
      // Check if API is available for email functionality
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        clearTimeout(emailTimeout);
        setLoading(false);
        setMessage({ 
          type: 'error', 
          text: 'Email functionality is not available in production mode. Please download PDF and send manually.' 
        });
        setTimeout(() => setMessage(null), 5000);
        return;
      }

      // Prepare bill data without saving
      const billData = {
        ...form,
        totals: calculateTotals(),
        company: {
          name: selectedCompanyFromState,
          logo: selectedCompanyDetails?.logo,
          qrCode: selectedCompanyDetails?.qrCode
        }
      };
      
      const pdfResponse = await Promise.race([
        api.post('/creditbills/generate-pdf-unsaved', { billData }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('PDF generation timed out')), 15000)
        )
      ]);
      
      // Check if response has the expected structure
      if (!pdfResponse?.data?.data?.pdf) {
        throw new Error('Invalid PDF response structure');
      }
      
      const pdfBase64 = pdfResponse.data.data.pdf;
      const emailData = {
        invoiceNo: form.invoiceNo,
        customerEmail: form.customerEmail,
        email: form.customerEmail,
        emailTo: form.customerEmail,
        subject: `Credit Bill #${form.invoiceNo} - ${selectedCompanyFromState || ''}`,
        body: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #b39eb5; border-bottom: 2px solid #b39eb5; padding-bottom: 10px;">
              Credit Bill from ${selectedCompanyFromState || ''}
            </h2>
            
            <p>Dear ${form.customerName},</p>
            
            <p>Please find attached your credit bill for the purchased items.</p>
            
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #333;">Bill Details:</h3>
              <ul style="margin: 10px 0; padding-left: 20px;">
                <li><strong>Bill Number:</strong> ${form.invoiceNo}</li>
                <li><strong>Customer ID:</strong> ${form.customerId}</li>
                <li><strong>Customer Name:</strong> ${form.customerName}</li>
                <li><strong>Date:</strong> ${form.date}</li>
                <li><strong>Payment Terms:</strong> ${form.paymentTerms}</li>
                <li><strong>Subtotal:</strong> ₹${calculateTotals().totalTaxableValue.toFixed(2)}</li>
                <li><strong>CGST:</strong> ₹${calculateTotals().totalCGST.toFixed(2)}</li>
                <li><strong>SGST:</strong> ₹${calculateTotals().totalSGST.toFixed(2)}</li>
                <li><strong>Grand Total:</strong> ₹${calculateTotals().grandTotal.toFixed(2)}</li>
                <li><strong>Status:</strong> ${form.cancelled ? 'Cancelled' : 'Active'}</li>
              </ul>
            </div>
            
            ${form.dueDates && form.dueDates.length > 0 ? `
            <div style="background-color: #e8f5e8; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #28a745;">Payment Due Dates:</h3>
              <ul style="margin: 10px 0; padding-left: 20px;">
                ${form.dueDates.map((due, index) => 
                  `<li><strong>Due Date ${index + 1}:</strong> ${due.date}</li>`
                ).join('')}
              </ul>
            </div>
            ` : ''}
            
            <p><strong>Payment Instructions:</strong></p>
            <ul style="margin: 10px 0; padding-left: 20px;">
              <li>Please make payment by the due dates mentioned above</li>
              <li>Payment can be made through bank transfer or other agreed methods</li>
              <li>For any queries regarding payment, please contact us immediately</li>
            </ul>
            
            <p>If you have any questions, please don't hesitate to contact us.</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              <p style="margin: 0;"><strong>Best regards,</strong><br>
              ${selectedCompanyFromState || ''}<br>
              Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001<br>
              Mobile: 8547014116 | Email: wyenfos@gmail.com<br>
              Website: www.wyenfos.com</p>
            </div>
          </div>
        `,
        pdfBase64: pdfBase64,
      };
      

      
      // Add timeout for the API request
      await Promise.race([
        api.post('/creditbills/send-email-unsaved', emailData),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Email request timed out')), 15000)
        )
      ]);
      

      clearTimeout(emailTimeout);
      setMessage({ type: 'success', text: 'Credit bill emailed successfully!' });
    } catch (error) {
      console.error('Failed to send email:', error);
      clearTimeout(emailTimeout);
      
      // Get the actual error message from the server response
      const errorMessage = error.response?.data?.message || error.message;
      setMessage({
        type: 'error',
        text: `Failed to send email: ${errorMessage}`,
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  }, [form, selectedCompanyFromState, selectedCompanyDetails?.logo, selectedCompanyDetails?.qrCode, validateForm, calculateTotals]);

  const printBill = useCallback(async () => {

    try {
      if (!form.items.length) {
        setMessage({ type: 'error', text: 'Add at least one item' });
        setTimeout(() => setMessage(null), 3000);
        return;
      }
      if (!validateForm()) return;

      // Use browser print directly - no need for API
      console.log('🖨️ Opening browser print dialog...');
      setMessage({ 
        type: 'info', 
        text: 'Opening print dialog...' 
      });
      setTimeout(() => setMessage(null), 2000);
      
      // Small delay to show the message, then print
      setTimeout(() => {
        window.print();
      }, 500);
      
    } catch (error) {
      console.error('Print error:', error);
      setMessage({
        type: 'error',
        text: `Failed to open print dialog: ${error.message}`,
      });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  }, [form, validateForm]);

  const downloadPDF = useCallback(async () => {
    if (!form.items.length) {
      setMessage({ type: 'error', text: 'Add at least one item' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      // Check if API is available for PDF generation
      if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
        // API not available - use browser print as fallback
        console.log('📄 API not available, using browser print as PDF alternative');
        
        setMessage({ 
          type: 'info', 
          text: 'PDF generation via API not available. Opening print dialog - you can save as PDF from there.' 
        });
        setTimeout(() => setMessage(null), 5000);
        
        // Trigger browser print dialog (user can save as PDF)
        window.print();
        return;
      }

      // Prepare bill data without saving
      const billData = {
        ...form,
        totals: calculateTotals(),
        company: {
          name: selectedCompanyFromState,
          logo: selectedCompanyDetails?.logo,
          qrCode: selectedCompanyDetails?.qrCode
        }
      };
      
      console.log('📄 Generating PDF via API...');
      const pdfResponse = await Promise.race([
        api.post('/creditbills/generate-pdf-unsaved', { billData }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('PDF generation timed out')), 30000)
        )
      ]);
      
      // Check if response has the expected structure
      if (!pdfResponse?.data?.data?.pdf) {
        throw new Error('Invalid PDF response structure');
      }
      
      const pdfBase64 = pdfResponse.data.data.pdf;

      // Download the PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdfBase64}`;
      link.download = `CreditBill_${form.invoiceNo || 'Draft'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setMessage({ type: 'success', text: 'PDF downloaded successfully!' });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      
      // Fallback to browser print on any error
      setMessage({
        type: 'warning',
        text: `PDF generation failed. Opening print dialog - you can save as PDF from there.`,
      });
      
      // Open browser print as fallback
      setTimeout(() => {
        window.print();
      }, 1000);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  }, [form, selectedCompanyFromState, selectedCompanyDetails?.logo, selectedCompanyDetails?.qrCode, validateForm, calculateTotals]);

  const resetForm = useCallback(async () => {
    try {
      const newInvoiceNo = await generateInvoiceNumber();
      dispatch({ type: 'SET_FORM', form: { ...initialFormState, invoiceNo: newInvoiceNo } });
      setHasUserInteracted(false); // Reset user interaction flag
      setErrors({
        customerId: '',
        customerName: '',
        customerAddress: '',
        customerPhone: '',
        customerEmail: '',
        customerGSTIN: '',
        invoiceNo: '',
        date: '',
        paymentTerms: '',
        dueDates: [{ id: uuidv4(), date: '' }],
        items: [{ id: uuidv4(), code: '', description: '', hsnSac: '', quantity: '', rate: '', gstRate: '', amountToPay: '' }],
        overallAmountToPay: '',
        overallRemainingBalance: '',
      });
      setCurrentBillId(null);
    } catch (err) {
      setMessage({ type: 'error', text: 'Reset failed' });
      setTimeout(() => setMessage(null), 3000);
    }
  }, [initialFormState, generateInvoiceNumber]);

  const handleEdit = useCallback((bill) => {
    if (!bill) {
      setMessage({ type: 'error', text: 'No bill data provided' });
      return;
    }
    if (!editPermissions[bill._id]) {
      setMessage({ type: 'error', text: 'Admin permission required' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const updatedForm = {
      ...form,
      customerId: bill.customerId || '',
      customerName: bill.customerName || '',
      customerAddress: bill.customerAddress || '',
      customerPhone: bill.customerPhone || '',
      customerEmail: bill.customerEmail || '',
      customerGSTIN: bill.customerGSTIN || '',
      invoiceNo: bill.invoiceNo || '',
      date: bill.date ? new Date(bill.date).toISOString().slice(0, 10) : '',
      dueDates: (bill.dueDates || []).map((d) => ({
        id: d.id || uuidv4(),
        date: d.date ? new Date(d.date).toISOString().slice(0, 10) : '',
        reminder: d.reminder || false,
      })),
      items: (bill.items || []).map((item) => ({
        id: item.id || uuidv4(),
        code: item.code || '',
        description: item.description || '',
        hsnSac: item.hsnSac || '',
        quantity: item.quantity?.toString() || '',
        unit: item.unit || 'NOS',
        rate: item.rate?.toString() || '',
        gstRate: item.gstRate?.toString() || '0',
        cgstRate: item.cgstRate?.toString() || '0',
        cgstAmount: item.cgstAmount?.toString() || '0',
        sgstRate: item.sgstRate?.toString() || '0',
        sgstAmount: item.sgstAmount?.toString() || '0',
        igstRate: item.igstRate?.toString() || '0',
        igstAmount: item.igstAmount?.toString() || '0',
        taxableValue: item.taxableValue?.toString() || '',
        total: item.total?.toString() || '',
        amountToPay: item.amountToPay?.toString() || '',
        balanceAmount: item.balanceAmount?.toString() || '',
      })),
      paymentTerms: bill.paymentDetails?.mode || 'Cash',
      isOtherState: bill.isOtherState || false,
      applyRoundOff: bill.applyRoundOff || false,
      cancelled: bill.cancelled || false,
      remarks: bill.remarks || '',
      company: bill.company || form.company,
      createdBy: currentUserEmail,
      lastUpdatedBy: currentUserEmail,
      paymentDetails: {
        mode: bill.paymentDetails?.mode || 'Cash',
        status: bill.paymentDetails?.status || 'Paid',
        dueDate: bill.paymentDetails?.dueDate || '',
      },
      overallAmountToPay: bill.totals?.amountToPay?.toString() || '0',
      overallRemainingBalance: bill.totals?.remainingBalance?.toString() || '0',
    };

    dispatch({ type: 'SET_FORM', form: updatedForm });
    setCurrentBillId(bill._id);

    setErrors({
      customerId: '',
      customerName: '',
      customerAddress: '',
      customerPhone: '',
      customerEmail: '',
      customerGSTIN: '',
      invoiceNo: '',
      date: '',
      paymentTerms: '',
      dueDates: updatedForm.dueDates.map((d) => ({ id: d.id, date: '' })),
      items: updatedForm.items.map((item) => ({
        id: item.id,
        code: '',
        description: '',
        hsnSac: '',
        rate: '',
        quantity: '',
        gstRate: '',
        amountToPay: '',
      })),
      overallAmountToPay: '',
      overallRemainingBalance: '',
    });
  }, [form, editPermissions, currentUserEmail]);

  const requestEditBill = useCallback((billId) => {
    const bill = savedBills.find((b) => b._id === billId);
    if (bill) {
      if (editPermissions[bill._id]) handleEdit(bill);
      else {
        setPendingEditBill(bill);
        setShowPermissionModal(true);
      }
    }
  }, [savedBills, editPermissions, handleEdit]);

const requestEditPermission = useCallback(async () => {
  if (!pendingEditBill) return false;
  
  try {
    setLoading(true);
    
    const requestData = {
      type: 'edit_request',
      billType: 'creditbill',
      billId: pendingEditBill._id,
      billData: pendingEditBill,
      requestedBy: currentUserEmail,
      requestedByRole: currentUserRole,
      reason: 'Staff requested permission to edit credit bill',
      invoiceNo: pendingEditBill.invoiceNo,
      customerName: pendingEditBill.customerName,
    };

    // Use Firebase for request management
    if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
      console.log('🔥 Sending edit request via Firebase...');
      await FirebaseService.addRequest(requestData);
      
      setMessage({ 
        type: 'success', 
        text: `Edit request sent to admin for bill ${pendingEditBill.invoiceNo}. You will be notified once approved.` 
      });
      return true;
    } else {
      console.log('🌐 Sending edit request via API...');
      const response = await api.post('/notifications/request-permission', requestData);

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Edit request sent to admin for bill ${pendingEditBill.invoiceNo}. You will be notified once approved.` 
        });
        return true;
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Failed to send edit request' 
        });
        return false;
      }
    }
  } catch (error) {
    console.error('Failed to send edit request:', error);
    setMessage({ 
      type: 'error', 
      text: `Failed to send edit request: ${error.message}` 
    });
    return false;
  } finally {
    setLoading(false);
    setTimeout(() => setMessage(null), 3000);
  }
}, [pendingEditBill, currentUserEmail, currentUserRole]);

const requestDeletePermission = useCallback(async () => {
  if (!pendingEditBill) return false;
  
  try {
    setLoading(true);
    
    const requestData = {
      type: 'delete_request',
      billType: 'creditbill',
      billId: pendingEditBill._id,
      billData: pendingEditBill,
      requestedBy: currentUserEmail,
      requestedByRole: currentUserRole,
      reason: 'Staff requested permission to delete credit bill',
      invoiceNo: pendingEditBill.invoiceNo,
      customerName: pendingEditBill.customerName,
    };

    // Use Firebase for request management
    if (API_CONFIG.USE_FIREBASE_ONLY || !API_CONFIG.BASE_URL) {
      console.log('🔥 Sending delete request via Firebase...');
      await FirebaseService.addRequest(requestData);
      
      setMessage({ 
        type: 'success', 
        text: `Delete request sent to admin for bill ${pendingEditBill.invoiceNo}. You will be notified once approved.` 
      });
      return true;
    } else {
      console.log('🌐 Sending delete request via API...');
      const response = await api.post('/notifications/request-permission', requestData);

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Delete request sent to admin for bill ${pendingEditBill.invoiceNo}. You will be notified once approved.` 
        });
        return true;
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Failed to send delete request' 
        });
        return false;
      }
    }
  } catch (error) {
    console.error('Failed to send delete request:', error);
    setMessage({ 
      type: 'error', 
      text: `Failed to send delete request: ${error.message}` 
    });
    return false;
  } finally {
    setLoading(false);
    setTimeout(() => setMessage(null), 3000);
  }
}, [pendingEditBill, currentUserEmail, currentUserRole]);

  const handlePermissionResponse = useCallback((approved) => {
    if (approved && pendingEditBill) {
      setEditPermissions((prev) => ({ ...prev, [pendingEditBill._id]: true }));
      handleEdit(pendingEditBill);
    } else {
      setMessage({ type: 'error', text: 'Permission denied' });
    }
    setShowPermissionModal(false);
    setPendingEditBill(null);
  }, [pendingEditBill, handleEdit]);

  const totals = calculateTotals();

  // Debug logging to identify the issue


  return (
    <ErrorBoundary>
      <div className="creditbill-page-wrapper">
        {form.cancelled && (
          <div className="cancelled-watermark">
            <img src={cancelledWatermark} alt="Cancelled" />
          </div>
        )}
        <div className="invoice-container">
          {message && (
            <div className={`message ${message.type}`}>
              <p>{message.text}</p>
              <button type="button" onClick={() => setMessage(null)} aria-label="Close">
                ✕
              </button>
            </div>
          )}

          {showPermissionModal && pendingEditBill && (
            <div className="modal">
              <div className="modal-content">
                <h3>Edit Permission Request</h3>
                <p>Approve editing bill {pendingEditBill.invoiceNo}?</p>
                <div className="modal-buttons">
                  <button type="button" onClick={() => handlePermissionResponse(true)}>
                    Approve
                  </button>
                  <button type="button" onClick={() => handlePermissionResponse(false)}>
                    Deny
                  </button>
                </div>
              </div>
            </div>
          )}

          {showManualEntryModal && (
            <div className="modal">
              <div className="modal-content manual-entry-modal">
                <h3>📝 Manual Product Entry</h3>
                <p className="modal-subtitle">Enter product details manually when product rates have errors or product is not found in system.</p>
                
                <div className="manual-entry-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Product Description *</label>
                      <input
                        type="text"
                        value={manualEntryData.description}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Enter product description"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Item Code</label>
                      <input
                        type="text"
                        value={manualEntryData.code}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, code: e.target.value }))}
                        placeholder="Enter item code"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>HSN/SAC Code</label>
                      <input
                        type="text"
                        value={manualEntryData.hsnSac}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, hsnSac: e.target.value }))}
                        placeholder="Enter HSN/SAC code"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantity *</label>
                      <input
                        type="number"
                        value={manualEntryData.quantity}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                        min="1"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Rate (₹) *</label>
                      <input
                        type="number"
                        value={manualEntryData.rate}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, rate: Math.max(0, parseFloat(e.target.value) || 0) }))}
                        step="0.01"
                        min="0"
                        placeholder="Enter rate"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>GST Rate (%) *</label>
                      <input
                        type="number"
                        value={manualEntryData.taxRate}
                        onChange={(e) => setManualEntryData(prev => ({ ...prev, taxRate: Math.max(0, parseFloat(e.target.value) || 18) }))}
                        step="0.01"
                        min="0"
                        placeholder="Enter GST rate"
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="manual-entry-preview">
                    <h4>Preview:</h4>
                    <div className="preview-item">
                      <strong>{manualEntryData.description || 'Product Description'}</strong>
                      <div className="preview-details">
                        <span>Code: {manualEntryData.code || 'N/A'}</span>
                        <span>HSN: {manualEntryData.hsnSac || 'N/A'}</span>
                        <span>Qty: {manualEntryData.quantity}</span>
                        <span>Rate: ₹{manualEntryData.rate || '0.00'}</span>
                        <span>GST: {manualEntryData.taxRate || '18'}%</span>
                        <span>Total: ₹{((manualEntryData.quantity || 1) * (manualEntryData.rate || 0)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-buttons">
                  <button type="button" onClick={saveManualEntry} className="btn-primary">
                    ✅ Save Product
                  </button>
                  <button type="button" onClick={closeManualEntryModal} className="btn-secondary">
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bill-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>
            {/* Logo and Company Address Section - Positioned on the left side */}
            <div className="header-left-section">
              <div className="company-logo-container-header">
                <img
                  src={getCompanyLogo()}
                  alt=""
                  className="company-logo-header"
                  onError={(e) => {
                    console.log('🚫 Logo failed to load for:', selectedCompanyFromState);
                    console.log('🔄 Switching to default logo');
                    e.target.src = defaultLogo;
                  }}
                  onLoad={() => {
                    console.log('✅ Logo loaded successfully for:', selectedCompanyFromState);
                  }}
                  style={{
                    display: 'block',
                    visibility: 'visible',
                    maxWidth: '120px',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <div className="company-address-header">
                <h4 className="company-name-header">{selectedCompanyFromState}</h4>
                <p className="address-text-header">{selectedCompanyDetails.address}</p>
                <div className="company-details-row">
                  {selectedCompanyDetails.GSTIN && <p className="gstin-text-header">GSTIN: {selectedCompanyDetails.GSTIN}</p>}
                  {selectedCompanyDetails.email && <p className="contact-text-header">{selectedCompanyDetails.email}</p>}
                </div>
                <div className="company-details-row">
                  {selectedCompanyDetails.mobile && <p className="contact-text-header mobile-number-left">{selectedCompanyDetails.mobile}</p>}
                  {selectedCompanyDetails.website && <p className="contact-text-header">{selectedCompanyDetails.website}</p>}
                </div>
              </div>
            </div>

            {/* Center logo for dual-logo companies */}
            {(() => {
              const logoConfig = getLogoConfiguration(selectedCompanyFromState);
              console.log('🔍 Logo Configuration for', selectedCompanyFromState, ':', logoConfig);
              return logoConfig.isDualLogo && logoConfig.centerLogo ? (
                <div className="center-logo-container" style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: '0 0 auto',
                  padding: '0 20px'
                }}>
                  <img
                    src={logoConfig.centerLogo}
                    alt={logoConfig.centerLogoAlt}
                    className="center-logo-header"
                    onError={(e) => {
                      console.log('🚫 Center logo failed to load for:', selectedCompanyFromState);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('✅ Center logo loaded successfully for:', selectedCompanyFromState);
                    }}
                    style={{
                      display: 'block',
                      visibility: 'visible',
                      maxWidth: '100px',
                      maxHeight: '100px',
                      objectFit: 'contain',
                      padding: '8px'
                    }}
                  />
                </div>
              ) : null;
            })()}
            
            {/* Bill Information - Right side */}
            <div className="bill-info-header">
              <h2>CREDIT BILL{form.cancelled ? ' (CANCELLED)' : ''}</h2>
              <p className="bill-number">CREDIT BILL No: {form.invoiceNo || `${companyPrefixes[selectedCompanyFromState] || 'WNF'}-${Math.floor(Math.random() * 1000) + 1}`}</p>
              <p className="date">Date: {form.date || new Date().toLocaleDateString('en-IN')}</p>
            </div>
          </div>

          <hr className="invoice-divider" />
          <div className="customer-search no-print">
            <div className="search-input-group">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Enter full customer name to search"
                className="form-control credit-bill-input"
                disabled={form.cancelled || loading}
              />
              <button
                className="search-button no-print"
                onClick={handleManualSearch}
                disabled={!customerSearch.trim() || loading}
              >
                🔍 Search
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((customer, index) => (
                  <div
                    key={customer._id || customer.customerId || `customer-${index}`}
                    onClick={() => selectCustomer(customer)}
                    className="search-result-item"
                  >
                    {customer.customerId} - {customer.customerName}
                    {customer.customerContact?.phone && ` - ${customer.customerContact.phone}`}
                    {customer.customerContact?.email && ` - ${customer.customerContact.email}`}
                  </div>
                ))}
              </div>
            )}
            {customerSearch.trim() && searchResults.length === 0 && (
              <div className="customer-info-section">
                <div className="customer-not-found-info">
                  <p className="info-message">
                    ℹ️ No customer found with name: <strong>{customerSearch}</strong>
                  </p>
                  <p className="info-suggestion">
                    You can continue by filling in the customer details manually below.
                  </p>
                </div>
              </div>
            )}
            {isCustomerLoading && <div className="loading-message">Loading customers...</div>}
          </div>
          <div className="section-title">Customer Information</div>
          <div className="customer-details">
            <div className="customer-form">
              <div className="credit-bill-input-group">
                <label>Customer ID:</label>
                <input
                  type="text"
                  name="customerId"
                  value={form.customerId}
                  onChange={handleChange}
                  placeholder="Customer ID (e.g., CUST-1, CUST-2)"
                  className={`credit-bill-input ${errors.customerId ? 'error-field' : ''}`}
                  disabled={form.cancelled || loading || isCustomerLoading}
                />
                {errors.customerId && <div className="error-message">{errors.customerId}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>Customer Name:</label>
                <input
                  type="text"
                  name="customerName"
                  value={form.customerName}
                  onChange={handleChange}
                  placeholder="Customer Name"
                  className={`credit-bill-input ${errors.customerName ? 'error-field' : ''}`}
                  disabled={form.cancelled || loading || isCustomerLoading}
                />
                {errors.customerName && <div className="error-message">{errors.customerName}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>Date:</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  className={`credit-bill-input ${errors.date ? 'error-border' : ''}`}
                  disabled={form.cancelled || loading}
                />
                {errors.date && <div className="error">{errors.date}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>Address:</label>
                <input
                  type="text"
                  name="customerAddress"
                  value={form.customerAddress}
                  onChange={handleChange}
                  placeholder="Address"
                  className={`credit-bill-input ${errors.customerAddress ? 'error-border' : ''}`}
                  disabled={form.cancelled || loading}
                />
                {errors.customerAddress && <div className="error">{errors.customerAddress}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>Phone:</label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={form.customerPhone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className={`credit-bill-input ${errors.customerPhone ? 'error-border' : ''}`}
                  disabled={form.cancelled || loading}
                />
                {errors.customerPhone && <div className="error">{errors.customerPhone}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="customerEmail"
                  value={form.customerEmail}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className={`credit-bill-input ${errors.customerEmail ? 'error-border' : ''}`}
                  disabled={form.cancelled || loading}
                />
                {errors.customerEmail && <div className="error">{errors.customerEmail}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>GSTIN:</label>
                <input
                  type="text"
                  name="customerGSTIN"
                  value={form.customerGSTIN}
                  onChange={handleChange}
                  placeholder="GSTIN"
                  className={`credit-bill-input ${errors.customerGSTIN ? 'error-border' : ''}`}
                  disabled={form.cancelled || loading}
                />
                {errors.customerGSTIN && <div className="error">{errors.customerGSTIN}</div>}
              </div>

              <div className="credit-bill-input-group">
                <label>Payment Terms:</label>
                <select
                  name="paymentTerms"
                  value={form.paymentTerms}
                  onChange={handleChange}
                  className={`credit-bill-input ${errors.paymentTerms ? 'error-border' : ''}`}
                  disabled={form.cancelled || loading}
                >
                  <option value="Cash">Cash</option>
                  <option value="Credit">Credit</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">CARD</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
                {errors.paymentTerms && <div className="error">{errors.paymentTerms}</div>}
              </div>
              
              {/* Save Customer Button */}
              <div className="save-customer-section no-print">
                <button
                  type="button"
                  onClick={saveCustomerDetails}
                  className="save-customer-btn"
                  disabled={loading || !form.customerName.trim()}
                  title="Save customer details to database"
                >
                  💾 Save Customer Details
                </button>
                <div className="save-customer-info">
                  <small>💡 Save customer information for future use. Customer ID will be auto-generated if not provided.</small>
                </div>
              </div>
            </div>
          </div>

          {/* Print-visible Due Dates Section */}
          <div className="due-dates-section-print">
            <h3>Installment Due Dates:</h3>
            <div className="due-dates-list-print">
              {form.dueDates.map((dueDate, index) => (
                <div key={dueDate.id} className="due-date-item-print">
                  <span><strong>Due Date {index + 1}:</strong></span>
                  <span>{dueDate.date ? new Date(dueDate.date).toLocaleDateString('en-IN') : 'Not Set'}</span>
                </div>
              ))}
            </div>
            {form.dueDates.length > 0 && (
              <p className="due-date-note">
                <em>Due date 3 must be today or later</em>
              </p>
            )}
          </div>

          <div className="section-title">Installment Due Dates</div>
          <div className="due-dates-group">
            {form.dueDates.map((dueDate, index) => (
              <div key={dueDate.id} className="due-date-row">
                <label>
                  Due Date {index + 1}:
                  {editingDueDateIndex === index ? (
                    <div className="due-date-edit-container">
                      <input
                        type="date"
                        name={`dueDates[${index}].date`}
                        value={dueDate.date || ''}
                        onChange={(e) => handleDueDateChange(index, e.target.value)}
                        className={`credit-bill-input ${errors.dueDates[index]?.date ? 'error-border' : ''}`}
                        disabled={form.cancelled || loading}
                        autoFocus
                      />
                      <div className="due-date-buttons">
                        <button
                          type="button"
                          onClick={() => confirmDueDate(index)}
                          className="ok-due-date-btn"
                          disabled={loading}
                        >
                          OK
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditingDueDate}
                          className="cancel-due-date-btn"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span
                      className={`editable-date ${errors.dueDates[index]?.date ? 'error-border' : ''}`}
                      onClick={() => startEditingDueDate(index)}
                      onKeyDown={(e) => e.key === 'Enter' && startEditingDueDate(index)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Edit due date ${index + 1}`}
                    >
                      {dueDate.date ? new Date(dueDate.date).toLocaleDateString('en-IN') : 'Set due date'}
                    </span>
                  )}
                  {errors.dueDates[index]?.date && <div className="error">{errors.dueDates[index].date}</div>}
                </label>
                {dueDate.date && editingDueDateIndex !== index && (
                  <button
                    type="button"
                    onClick={() => toggleReminder(index, dueDate.date)}
                    className={`reminder-btn ${dueDate.reminder ? 'active' : ''}`}
                    disabled={form.cancelled || loading}
                  >
                    {dueDate.reminder ? '✅ Reminder Set' : '⏰ Set Reminder'}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addDueDate}
              className="add-due-date-btn"
              disabled={form.cancelled || loading}
            >
              + Add Due Date
            </button>
          </div>

          <div className="tax-options">
            <label>
              <input
                type="checkbox"
                name="isOtherState"
                checked={form.isOtherState}
                onChange={handleChange}
                disabled={form.cancelled || loading}
              />
              Apply inter-state tax (IGST)
            </label>
            <label>
              <input
                type="checkbox"
                name="applyRoundOff"
                checked={form.applyRoundOff}
                onChange={handleChange}
                disabled={form.cancelled || loading}
              />
              Apply round-off
            </label>
            <label>
              <input
                type="checkbox"
                name="cancelled"
                checked={form.cancelled}
                onChange={handleChange}
                disabled={loading}
              />
              Mark as cancelled
            </label>
          </div>

        <div className="credit-bill-items">
          <div className="items-header">
  <h3>ITEMS</h3>
            <div className="items-instructions">
              <span className="instruction-text">
                💡 <strong>Product Edit Instructions:</strong> Use the search icon (🔍) to find products, or click the edit icon (✏️) to manually enter product details. 
                Click the settings icon (⚙️) to open field editors for price, GST, HSN, and quantity. All fields in the table below are also directly editable to correct any purchase admin errors.
              </span>
            </div>
            <button
              type="button"
              className={`manual-edit-mode-btn ${manualEditMode ? 'active' : ''}`}
              onClick={() => setManualEditMode(!manualEditMode)}
              disabled={form.cancelled || loading}
            >
              {manualEditMode ? '✅ Exit Manual Edit Mode' : '📝 Manual Edit Mode - You can type product details directly'}
            </button>
          </div>
  <div className="billitems-section">
    <div className="col-sno">SI No</div>
    <div className="col-code">Item Code</div>
    <div className="col-name">Item Name</div>
    <div className="col-hsn">HSN/SAC</div>
    <div className="col-qty">Quantity</div>
    <div className="col-rate">Rate</div>
    <div className="col-total">Total</div>
    <div className="col-tax">Tax Rate (%)</div>
    <div className="col-action">Action</div>
  </div>

  {form.items.map((item, index) => (
    <div key={item.id || `item-${index}`} className="creditbillitemm-row">
      <div className="col-sno">{index + 1}</div>
      <div className="col-code">
        <input
          type="text"
          value={item.code}
          onChange={(e) => handleItemChange(index, { target: { name: 'code', value: e.target.value } })}
          className={errors.items[index]?.code ? 'error-border' : ''}
          disabled={form.cancelled || loading || !editModeToggle}
          placeholder={editModeToggle ? 'Enter item code' : 'Click ✏️ to edit'}
        />
        {errors.items[index]?.code && <div className="error">{errors.items[index].code}</div>}
      </div>
      
      <div className="col-name">
        <div className="product-input-container">
          <ProductSelector
            value={item.description}
            onChange={(value) => handleProductInput(value, index)}
            onProductSelect={(product) => handleProductSelect(product, index)}
            placeholder="Search products..."
            disabled={form.cancelled || loading}
            showEditMode={true}
            showFieldEditors={true}
          />
          <button
            type="button"
            className="manual-entry-btn"
            onClick={() => openManualEntryModal(index)}
            disabled={form.cancelled || loading}
            title="Enter product details manually"
          >
            📝 Manual
          </button>
        </div>
        {errors.items[index]?.description && <div className="error">{errors.items[index].description}</div>}
      </div>

      <div className="col-hsn">
        <input
          type="text"
          value={item.hsnSac}
          onChange={(e) => handleItemChange(index, { target: { name: 'hsnSac', value: e.target.value } })}
          className={errors.items[index]?.hsnSac ? 'error-border' : ''}
          disabled={form.cancelled || loading || !editModeToggle}
          placeholder={editModeToggle ? 'Enter HSN/SAC' : 'Click ✏️ to edit'}
        />
        {errors.items[index]?.hsnSac && <div className="error">{errors.items[index].hsnSac}</div>}
      </div>

      <div className="col-qty">
        <input
          type="number"
          value={item.quantity}
          onChange={(e) => handleItemChange(index, { target: { name: 'quantity', value: e.target.value } })}
          className={errors.items[index]?.quantity ? 'error-border' : ''}
          disabled={form.cancelled || loading || !editModeToggle}
          placeholder={editModeToggle ? 'Enter quantity' : 'Click ✏️ to edit'}
        />
        {errors.items[index]?.quantity && <div className="error">{errors.items[index].quantity}</div>}
      </div>

      <div className="col-rate">
        <input
          type="number"
          value={item.rate}
          onChange={(e) => handleItemChange(index, { target: { name: 'rate', value: e.target.value } })}
          className={errors.items[index]?.rate ? 'error-border' : ''}
          disabled={form.cancelled || loading || !editModeToggle}
          placeholder={editModeToggle ? 'Enter rate' : 'Click ✏️ to edit'}
        />
        {errors.items[index]?.rate && <div className="error">{errors.items[index].rate}</div>}
      </div>

      <div className="col-total">
        <input
          type="text"
          value={item.total}
          readOnly
          className="read-only"
        />
      </div>

      <div className="col-tax">
        <input
          type="number"
          value={item.gstRate}
          onChange={(e) => handleItemChange(index, { target: { name: 'gstRate', value: e.target.value } })}
          className={errors.items[index]?.gstRate ? 'error-border' : ''}
          disabled={form.cancelled || loading || !editModeToggle}
          placeholder={editModeToggle ? 'Enter GST %' : 'Click ✏️ to edit'}
        />
        {errors.items[index]?.gstRate && <div className="error">{errors.items[index].gstRate}</div>}
      </div>

      <div className="col-action">
        <div className="action-buttons-group">
          <button
            className={`edit-mode-toggle-btn ${editModeToggle ? 'active' : ''}`}
            onClick={() => {
              setEditModeToggle(!editModeToggle);
              setHasUserInteracted(true);
              // Clear validation errors for this item when enabling edit mode
              if (!editModeToggle) {
                setErrors(prev => ({
                  ...prev,
                  items: prev.items.map((itemErrors, idx) => 
                    idx === index 
                      ? { ...itemErrors, code: '', description: '', hsnSac: '', rate: '', quantity: '', gstRate: '', amountToPay: '' }
                      : itemErrors
                  )
                }));
              }
            }}
            disabled={form.cancelled || loading}
            title={editModeToggle ? 'Disable edit mode' : 'Enable edit mode'}
          >
            {editModeToggle ? '🔒' : '✏️'}
          </button>
          <button
            className="delete-item-btn"
            onClick={() => deleteItem(index)}
            disabled={form.cancelled || loading}
            aria-label={`Delete item ${index + 1}`}
          >
            🗑️
          </button>
        </div>
      </div>
      
      {/* Mobile trash button - shown only on mobile and tablet */}
      <div className="mobile-trash-button">
        <button
          className="delete-item-btn"
          onClick={() => deleteItem(index)}
          disabled={form.cancelled || loading}
          aria-label={`Delete item ${index + 1}`}
        >
          🗑️ Delete Item
        </button>
      </div>
    </div>
  ))}

  <button 
    className="add-itemm-btn no-print" 
    onClick={addItem} 
    disabled={form.cancelled || loading}
  >
    + Add Item
  </button>
</div>

          {/* Manual Field Adjustment Section */}
          {manualEditMode && (
            <div className="manual-field-adjustment">
              <div className="manual-field-header">
                <h4>🔧 Manual Field Adjustment</h4>
                <p>Edit these fields to correct purchase admin errors.</p>
              </div>
              
              <div className="manual-field-form">
                <div className="manual-field-row">
                  <div className="manual-field-group">
                    <label>Item Code:</label>
                    <input
                      type="text"
                      placeholder="Enter item code"
                      className="manual-field-input"
                    />
                  </div>
                  <div className="manual-field-group">
                    <label>HSN/SAC:</label>
                    <input
                      type="text"
                      placeholder="Enter HSN/SAC code"
                      className="manual-field-input"
                    />
                  </div>
                </div>
                
                <div className="manual-field-row">
                  <div className="manual-field-group">
                    <label>Quantity:</label>
                    <input
                      type="number"
                      placeholder="4"
                      className="manual-field-input"
                    />
                  </div>
                  <div className="manual-field-group">
                    <label>Rate (₹):</label>
                    <input
                      type="number"
                      placeholder="787"
                      className="manual-field-input"
                    />
                  </div>
                  <div className="manual-field-group">
                    <label>GST Rate (%):</label>
                    <input
                      type="number"
                      placeholder="0.03"
                      className="manual-field-input"
                    />
                  </div>
                </div>
                
                <div className="manual-field-buttons">
                  <button 
                    type="button" 
                    className="btn-save-manual"
                    onClick={() => {
                      // Save manual field adjustments
                      console.log('Saving manual field adjustments');
                      setManualEditMode(false);
                    }}
                  >
                    💾 Save Changes
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel-manual"
                    onClick={() => {
                      // Cancel manual field adjustments
                      console.log('Cancelling manual field adjustments');
                      setManualEditMode(false);
                    }}
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="totals-section">
            <h3>Totals</h3>
            <div className="totals-grid">
              <div className="totals-row">
                <span>Subtotal:</span>
                <span>₹{(totals.totalTaxableValue ?? 0).toFixed(2)}</span>
              </div>
              {form.isOtherState ? (
                <div className="totals-row">
                  <span>IGST:</span>
                  <span>₹{(totals.totalIGST ?? 0).toFixed(2)}</span>
                </div>
              ) : (
                <>
                  <div className="totals-row">
                    <span>CGST:</span>
                    <span>₹{(totals.totalCGST ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="totals-row">
                    <span>SGST:</span>
                    <span>₹{(totals.totalSGST ?? 0).toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="totals-row">
                <span>Total + Tax:</span>
                <span>₹{(totals.grandTotal - (totals.roundOff ?? 0)).toFixed(2)}</span>
              </div>
              {form.applyRoundOff && (
                <div className="totals-row">
                  <span>Round Off:</span>
                  <span>₹{(totals.roundOff ?? 0).toFixed(2)}</span>
                </div>
              )}
              <div className="totals-row highlight">
                <span>Grand Total:</span>
                <span>₹{(totals.grandTotal ?? 0).toFixed(2)}</span>
              </div>
              <div className="totals-row">
                <span>Amount to Pay:</span>
                <input
                  type="number"
                  name="overallAmountToPay"
                  value={form.overallAmountToPay || ''}
                  onChange={handleChange}
                  placeholder="Enter amount to pay"
                  className="amount-to-pay-input"
                  disabled={form.cancelled || loading}
                  step="0.01"
                  min="0"
                  max={totals.grandTotal}
                />
              </div>
              <div className="totals-row">
                <span>Remaining Balance:</span>
                <span>
                  ₹{form.overallRemainingBalance}
                </span>
              </div>
            </div>
          </div>

          <div className="remarks-section">
            <label>
              Remarks:
              <textarea
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                disabled={form.cancelled || loading}
              />
            </label>
          </div>

          <div className="action-buttons no-print">
            <button className="btn primary no-print" onClick={saveBill} disabled={loading}>
              {loading ? 'Saving...' : 'Save Bill'}
            </button>
            <button className="btn success no-print" onClick={downloadPDF} disabled={loading}>
              {loading ? 'Generating...' : 'Download PDF'}
            </button>
            <button className="btn success no-print" onClick={sendEmail} disabled={loading || !form.customerEmail}>
              {loading ? 'Sending...' : '📧 Email'}
            </button>
            <button className="btn secondary no-print" onClick={printBill} disabled={loading}>
            🖨️ Print
            </button>
            <button className="btn warning no-print" onClick={resetForm} disabled={loading}>
              Reset Form
            </button>
            <button className="btn no-print" onClick={() => navigate(-1)} disabled={loading}>
              Back
            </button>
          </div>

          <div className="futerr">
            <div className="futer-content">
              <div className="left-section">
                <div className="terms-section">
            <h4>Terms & Conditions</h4>
                  <div className="terms-content">
            <p>
              1. This credit bill is issued as per agreed terms.<br />
              2. Contact us within 7 days for discrepancies.<br />
              3. Amount credited can be adjusted against future invoices.
            </p>
            </div>
                </div>
                
                <div className="bank-details">
              <h4>Bank Details 
             
              </h4>
                  <div className="bank-container">
                    <div className="bank-contentcashbill">
                      <p><strong>Company name:</strong> {selectedCompanyFromState}</p>
                      {bankDetails.accountNumber ? (
                        <p><strong>Account number:</strong> {bankDetails.accountNumber}</p>
                      ) : (
                        <p><strong>Account number:</strong> <span style={{color: 'red'}}>❌ NOT SET - ADD TO FIREBASE</span></p>
                      )}
                      {bankDetails.ifsc ? (
                        <p><strong>IFSC:</strong> {bankDetails.ifsc}</p>
                      ) : (
                        <p><strong>IFSC:</strong> <span style={{color: 'red'}}>❌ NOT SET - ADD TO FIREBASE</span></p>
                      )}
                      {bankDetails.swiftCode && <p><strong>SWIFT code:</strong> {bankDetails.swiftCode}</p>}
                      {bankDetails.bankName ? (
                        <p><strong>Bank name:</strong> {bankDetails.bankName}</p>
                      ) : (
                        <p><strong>Bank name:</strong> <span style={{color: 'red'}}>❌ NOT SET - ADD TO FIREBASE</span></p>
                      )}
                      {bankDetails.branch ? (
                        <p><strong>Branch:</strong> {bankDetails.branch}</p>
                      ) : (
                        <p><strong>Branch:</strong> <span style={{color: 'red'}}>❌ NOT SET - ADD TO FIREBASE</span></p>
                      )}
                      {bankDetails.upiId && <p><strong>UPI ID:</strong> {bankDetails.upiId}</p>}
                    </div>
                    <div className="qr-code-section">
                      {qrCodeData ? (
                        <QRCodeCanvas 
                          value={qrCodeData} 
                          size={120} 
                          level="H" 
                          includeMargin 
                          className="qr-code"
                        />
                      ) : (
                        <div className="qr-code-placeholder">
                          <div className="qr-placeholder-box">
                            <span>QR</span>
                          </div>
                        </div>
                      )}
                      <p className="qr-code-label">SCAN TO PAY</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="signaturee">
              <strong>Authorized Signatory</strong><br />
              <strong>{selectedCompanyFromState || ''}</strong>
            </div>
          </div>

          <div className="saved-bills">
            {message && <div className={`message ${message.type}`}>{message.text}</div>}
            <h3>Saved Credit Bills</h3>
            {savedBills.length > 0 ? (
              <div className="bills-table-container">
                <table className="saved-bills-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Customer</th>
                      <th>Customer ID</th>
                      <th>Date</th>
                      <th>Due Dates</th>
                      <th>Total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedBills.map((bill, index) => (
                      <tr key={bill._id || `bill-${index}`} className={currentBillId === bill._id ? 'current-bill' : ''}>
                        <td>{bill.invoiceNo}</td>
                        <td>{bill.customerName}</td>
                        <td>{bill.customerId || 'N/A'}</td>
                        <td>{bill.date ? new Date(bill.date).toLocaleDateString('en-IN') : 'N/A'}</td>
                        <td>
                          {bill.dueDates && bill.dueDates.length > 0 ? (
                            <div className="due-dates-display">
                              {bill.dueDates.map((dueDate, idx) => (
                                <div key={idx} className="due-date-display-item">
                                  {dueDate.date ? new Date(dueDate.date).toLocaleDateString('en-IN') : 'Not Set'}
                                </div>
                              ))}
                            </div>
                          ) : (
                            'No Due Dates'
                          )}
                        </td>
                        <td>{bill.totals?.grandTotal ? '₹' + parseFloat(bill.totals.grandTotal).toFixed(2) : 'N/A'}</td>
                        <td>
                          {editPermissions[bill._id] ? (
                            <button className="select-btn no-print" onClick={() => requestEditBill(bill._id)}>
                              Edit
                            </button>
                          ) : (
                            <button className="select-btn no-print" onClick={() => { setPendingEditBill(bill); requestEditPermission(); }}>
                              Request Edit
                            </button>
                          )}
                          <button
                            className="btn-danger no-print"
                            onClick={() => { setPendingEditBill(bill); requestDeletePermission(); }}
                          >
                            Request Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-bills-info">
                <p>No saved credit bills found for {selectedCompanyFromState}</p>
                <p style={{fontSize: '12px', color: '#6c757d', marginTop: '0.5rem'}}>
                  💡 Create and save credit bills to see them listed here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CreditBill;